import { useRef, useState } from 'react';
import { Field, FieldProps } from 'formik';
import { MAX_QUANTITY_DIGITS, applyLimits, isZeroValue } from './common';

type QuantityFieldProps = {
    name: string;
    id?: string;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    /** Extra work on blur — the voyage form uses this to recompute its totals. */
    onBlur?: (e: any) => void;
    /** Whole digits allowed before the decimal point. */
    maxWholeDigits?: number;
    /** Decimal places allowed. Unbounded when omitted. */
    maxDecimals?: number;
    /** Largest value the field may hold. */
    max?: number;
    /**
     * The unit shown to the right of the box. Passing it makes the field render
     * its own input group, which is what lets the limit notice sit under the
     * whole control — inside the caller's group it would be laid out as another
     * member of it. Without it the bare input is rendered, as before, and the
     * field stays silent about its limits.
     */
    unit?: string;
    /**
     * Whether the limit notice is shown under the box. Defaults to on when a
     * `unit` was given, since the field then owns its own input group and has
     * somewhere to put it, and off when the caller owns the layout — inside a
     * caller's input group the notice would be laid out as another member of it.
     * Pass it explicitly for a bare box that is not in a group and should still
     * say why a keystroke was refused.
     */
    showNotice?: boolean;
    /**
     * Commit the value instead of writing it straight to `name`. The blend
     * shares use this: each is derived from the other, so one cannot be stored
     * without recomputing its partner.
     */
    onValue?: (text: string) => void;
};

/**
 * Text input for a non-negative decimal: at most `maxWholeDigits` before the
 * point (MAX_QUANTITY_DIGITS by default), `maxDecimals` after it, and no more
 * than `max`.
 *
 * Used for every numeric box on the FuelEU forms, not only quantities — the OPS
 * reading, an E value and an LCV override differ from a tonnage only in how many
 * digits each can hold.
 *
 * The constraint is applied on the way in rather than as a Yup rule, so an
 * out-of-range value never lands in form state at all — the voyage form derives
 * its totals from these fields as they change, and a rejected-but-present value
 * would still feed those. A "-" simply never appears as the user types.
 *
 * Correcting the entry that way is silent, though, so it is paired with a notice
 * naming whichever limit acted. Otherwise a digit that does not appear reads as
 * a dropped keystroke rather than as a field holding its bounds. The notice is
 * about the keystroke, not about the value, so it clears as soon as the entry is
 * back inside its limits and again when focus leaves the field — what is left in
 * the box by then is always valid, and an error message under a valid value
 * reads as a form that cannot be submitted.
 *
 * Deliberately controlled (`value={field.value}`). The voyage inputs this
 * replaces were uncontrolled and committed on blur only, which meant a
 * programmatic reset left stale text on screen.
 *
 * A default 0 is wiped on focus so it can be typed over, and put back on blur
 * if nothing was typed — see `clearedZero` for why that is tracked rather than
 * inferred from the box being empty.
 */
const QuantityField = ({
    name,
    id,
    placeholder,
    className = 'form-control',
    disabled,
    onBlur,
    maxWholeDigits = MAX_QUANTITY_DIGITS,
    maxDecimals,
    max,
    unit,
    showNotice,
    onValue,
}: QuantityFieldProps) => {
    /**
     * Whether focus cleared a 0 that still owes a restore. Restoring on "blurred
     * while empty" alone would be wrong twice over: these fields are used with
     * both 0 and null initial values, so a null one would silently acquire a 0
     * the user never entered, and a user who deliberately empties a box would
     * have it refilled under them.
     */
    const clearedZero = useRef(false);

    /** Which limit last had to correct the entry, if any. */
    const [notice, setNotice] = useState<string | null>(null);

    const limits = { wholeDigits: maxWholeDigits, decimals: maxDecimals, max };

    return (
        <Field name={name}>
            {({ field, form }: FieldProps) => {
                const commit = (text: string) => {
                    if (onValue) onValue(text);
                    else form.setFieldValue(field.name, text);
                };

                const input = (
                    <input
                        type="text"
                        inputMode="decimal"
                        id={id ?? name}
                        name={field.name}
                        className={className}
                        disabled={disabled}
                        placeholder={placeholder}
                        value={field.value ?? ''}
                        onFocus={() => {
                            if (isZeroValue(field.value)) {
                                clearedZero.current = true;
                                setNotice(null);
                                commit('');
                            }
                        }}
                        onChange={(e) => {
                            // Something was typed, so there is no longer a 0 owed.
                            clearedZero.current = false;
                            const result = applyLimits(e.target.value, limits);
                            setNotice(result.notice);
                            commit(result.text);
                        }}
                        onBlur={(e) => {
                            const { text } = applyLimits(e.target.value, limits);
                            const restoring = clearedZero.current && text === '';
                            clearedZero.current = false;
                            // The notice explains a keystroke that was dropped as it
                            // was typed, so it has said what it had to say by the time
                            // focus leaves. The box cannot be holding an out-of-limit
                            // value at this point — it was corrected on the way in — so
                            // leaving the notice up would mark a valid entry as an error.
                            setNotice(null);
                            // Set on the event too, not just in form state: the voyage
                            // form's onBlur recomputes its totals from this value.
                            e.target.value = restoring ? '0' : text;
                            if (restoring) {
                                // The number, not "0": this is the value the
                                // field started with and the payload sends on.
                                if (onValue) onValue('0');
                                else form.setFieldValue(field.name, 0);
                            }
                            field.onBlur(e);
                            if (onBlur) onBlur(e);
                        }}
                    />
                );

                const noticeShown = showNotice ?? unit !== undefined;
                const noticeLabel = noticeShown && notice && (
                    <label className="error d-block">{notice}</label>
                );

                if (unit === undefined) {
                    if (!noticeLabel) return input;
                    return (
                        <>
                            {input}
                            {noticeLabel}
                        </>
                    );
                }

                return (
                    <>
                        <div className="input-group mb-2">
                            {input}
                            <div className="input-group-text">{unit}</div>
                        </div>
                        {noticeLabel}
                    </>
                );
            }}
        </Field>
    );
};

export default QuantityField;
